import {
  Box,
  Flex,
  HStack,
  Kbd,
  Spinner,
  Text,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import { RiFlagLine, RiSearchLine } from "@remixicon/react";
import {
  Action,
  ActionImpl,
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  KBarSearch,
  useKBar,
  useMatches,
  useRegisterActions,
  VisualState,
} from "kbar";
import { forwardRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { Avatar } from "@/common/components/Avatar";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useUsersSearch } from "@/common/data/useUsers";
import { useDebounce } from "@/common/hooks/useDebounce";
import { useCompaniesSearch } from "@/company/data/useCompaniesSearch";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useFlag } from "@reflag/react-sdk";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";
import { CompanyUrl, FeatureUrl, UserUrl } from "@reflag/shared/urls";

export const COMMAND_BAR_OPEN_EVENT = "reflag:open-command-bar";

export function openCommandBar() {
  window.dispatchEvent(new Event(COMMAND_BAR_OPEN_EVENT));
}

const MAX_RESULTS_PER_TYPE = 5;
const actionSections = {
  flags: { name: "Flags", priority: 3 },
  companies: { name: "Companies", priority: 2 },
  users: { name: "Users", priority: 1 },
} as const;

type CommandResultType = "flag" | "company" | "user";

export function CommandBar() {
  const { isEnabled } = useFlag("command-bar");

  if (!isEnabled) {
    return null;
  }

  return (
    <KBarProvider
      options={{
        disableScrollbarManagement: true,
        toggleShortcut: "$mod+k",
      }}
    >
      <CommandBarInner />
    </KBarProvider>
  );
}

function CommandBarInner() {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const { query, searchQuery, visualState } = useKBar((state) => ({
    searchQuery: state.searchQuery,
    visualState: state.visualState,
  }));

  const trimmedSearch = searchQuery.trim();
  const debouncedSearch = useDebounce(trimmedSearch, SEARCH_DEBOUNCE_MS);
  const searchEnabled =
    visualState !== VisualState.hidden && debouncedSearch.length > 0;
  const listQuery = useMemo(() => ({ pageSize: MAX_RESULTS_PER_TYPE }), []);
  const queryOptions = useMemo(
    () => ({ enabled: searchEnabled }),
    [searchEnabled],
  );

  const {
    data: users = [],
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useUsersSearch(debouncedSearch, listQuery, queryOptions);

  const {
    data: companies = [],
    isFetching: isFetchingCompanies,
    isLoading: isLoadingCompanies,
  } = useCompaniesSearch(debouncedSearch, listQuery, queryOptions);

  const {
    data: flags = [],
    isFetching: isFetchingFlags,
    isLoading: isLoadingFlags,
  } = useFeatureNamesData(
    {
      filter: debouncedSearch,
      limit: MAX_RESULTS_PER_TYPE,
    },
    queryOptions,
  );

  const isDebouncing =
    trimmedSearch.length > 0 && trimmedSearch !== debouncedSearch;

  const actions = useMemo<Action[]>(() => {
    if (!searchEnabled || trimmedSearch !== debouncedSearch) {
      return [];
    }

    const flagActions = flags
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map<Action>((flag) => ({
        id: `command-flag-${flag.id}`,
        name: flag.name,
        subtitle: flag.key,
        keywords: [flag.id, flag.key].filter(Boolean).join(" "),
        section: actionSections.flags,
        icon: <CommandBarActionIcon type="flag" />,
        perform: () => {
          navigate(FeatureUrl(env, { id: flag.id, name: flag.name }));
        },
      }));

    const userActions = users
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map<Action>((user) => ({
        id: `command-user-${user.id}`,
        name: user.name || user.email || user.id,
        subtitle: [user.email, user.id].filter(Boolean).join(" • "),
        keywords: [user.id, user.name, user.email].filter(Boolean).join(" "),
        section: actionSections.users,
        icon: (
          <CommandBarActionIcon
            name={user.name || user.email || user.id}
            src={user.avatarUrl}
            type="user"
          />
        ),
        perform: () => {
          navigate(UserUrl(env, user.id));
        },
      }));

    const companyActions = companies
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map<Action>((company) => ({
        id: `command-company-${company.id}`,
        name: company.name || company.id,
        subtitle: company.name ? company.id : undefined,
        keywords: [company.id, company.name].filter(Boolean).join(" "),
        section: actionSections.companies,
        icon: (
          <CommandBarActionIcon
            name={company.name || company.id}
            src={company.avatarUrl}
            type="company"
          />
        ),
        perform: () => {
          navigate(CompanyUrl(env, company.id));
        },
      }));

    return [...flagActions, ...userActions, ...companyActions];
  }, [
    companies,
    debouncedSearch,
    env,
    flags,
    navigate,
    searchEnabled,
    trimmedSearch,
    users,
  ]);

  useRegisterActions(actions, [actions]);

  useEffect(() => {
    const openFromEvent = () => {
      query.setSearch("");
      query.setVisualState(VisualState.animatingIn);
    };

    window.addEventListener(COMMAND_BAR_OPEN_EVENT, openFromEvent);

    return () => {
      window.removeEventListener(COMMAND_BAR_OPEN_EVENT, openFromEvent);
    };
  }, [query]);

  useEffect(() => {
    if (visualState === VisualState.hidden && searchQuery) {
      query.setSearch("");
    }
  }, [query, searchQuery, visualState]);

  const isLoading =
    isDebouncing ||
    isLoadingFlags ||
    isFetchingFlags ||
    isLoadingUsers ||
    isFetchingUsers ||
    isLoadingCompanies ||
    isFetchingCompanies;

  return <CommandBarDialog isLoading={isLoading} search={trimmedSearch} />;
}

function CommandBarDialog({
  isLoading,
  search,
}: {
  isLoading: boolean;
  search: string;
}) {
  const overlayBg = useToken(
    "colors",
    useColorModeValue("blackAlpha.500", "blackAlpha.700"),
  );
  const panelBorderColor = useColorModeValue("transparent", "appBorder");
  const panelShadow = useColorModeValue("lg", "dark-lg");

  return (
    <KBarPortal>
      <KBarPositioner
        style={{
          alignItems: "flex-start",
          background: overlayBg,
          paddingTop: "12vh",
          zIndex: 1700,
        }}
      >
        <KBarAnimator style={{ maxWidth: 720, width: "100%" }}>
          <Box
            bg="appBackground"
            borderColor={panelBorderColor}
            borderWidth="1px"
            overflow="hidden"
            rounded="md"
            shadow={panelShadow}
          >
            <Box pos="relative">
              <Flex
                align="center"
                color="dimmed"
                h="14"
                left={4}
                pointerEvents="none"
                pos="absolute"
                top={0}
                zIndex={1}
              >
                <RiSearchLine size={20} />
              </Flex>
              <Box
                as={KBarSearch}
                _placeholder={{ color: "dimmed" }}
                bg="transparent"
                border={0}
                color="chakra-body-text"
                defaultPlaceholder="Search users, companies, and flags..."
                fontSize="lg"
                h="14"
                outline="none"
                pl="12"
                pr="20"
                w="full"
              />
              <HStack
                color="dimmed"
                h="14"
                pointerEvents="none"
                pos="absolute"
                right={4}
                top={0}
              >
                <Kbd>Esc</Kbd>
              </HStack>
            </Box>
            <Box borderTopColor="appBorder" borderTopWidth="1px" py={2}>
              <CommandBarResults isLoading={isLoading} search={search} />
            </Box>
          </Box>
        </KBarAnimator>
      </KBarPositioner>
    </KBarPortal>
  );
}

function CommandBarResults({
  isLoading,
  search,
}: {
  isLoading: boolean;
  search: string;
}) {
  const { results } = useMatches();

  if (!search) {
    return (
      <Text color="dimmed" px={4} py={8} textAlign="center">
        Type a name, email, key, or ID to search.
      </Text>
    );
  }

  if (results.length === 0 && isLoading) {
    return (
      <Flex align="center" color="dimmed" gap={2} justify="center" py={8}>
        <Spinner size="sm" />
        <Text>Searching...</Text>
      </Flex>
    );
  }

  if (results.length === 0) {
    return (
      <Text color="dimmed" px={4} py={8} textAlign="center">
        No results found.
      </Text>
    );
  }

  return (
    <KBarResults
      items={results}
      maxHeight={640}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <CommandBarSection title={item} />
        ) : (
          <CommandBarResult action={item} active={active} />
        )
      }
    />
  );
}

// kbar's KBarResults attaches a measureRef to these elements to compute row
// heights for its virtualized list, so they must forward refs to the DOM.
const CommandBarSection = forwardRef<HTMLParagraphElement, { title: string }>(
  function CommandBarSection({ title }, ref) {
    return (
      <Text
        ref={ref}
        color="dimmed"
        fontSize="sm"
        fontWeight="medium"
        pb={1.5}
        pt={3}
        px={5}
      >
        {title}
      </Text>
    );
  },
);

const CommandBarResult = forwardRef<
  HTMLDivElement,
  {
    action: ActionImpl;
    active: boolean;
  }
>(function CommandBarResult({ action, active }, ref) {
  const activeBg = useColorModeValue("gray.50", "gray.750");

  return (
    <Flex
      ref={ref}
      align="center"
      bg={active ? activeBg : undefined}
      cursor="pointer"
      gap={3}
      mx={2}
      px={3}
      py={2}
      rounded="md"
    >
      {action.icon}
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight="medium" isTruncated>
          {action.name}
        </Text>
        {action.subtitle && (
          <Text color="dimmed" fontSize="xs" isTruncated>
            {action.subtitle}
          </Text>
        )}
      </Box>
    </Flex>
  );
});

function CommandBarActionIcon({
  name,
  src,
  type,
}: {
  name?: string;
  src?: string | null;
  type: CommandResultType;
}) {
  const iconBg = useColorModeValue("gray.50", "gray.800");

  if (type === "user" || type === "company") {
    return (
      <Avatar
        borderColor="appBorder"
        borderRadius={type === "company" ? 4 : "full"}
        borderWidth={1}
        flexShrink={0}
        name={name}
        size="sm"
        src={src ?? undefined}
        type={type}
      />
    );
  }

  return (
    <Flex
      align="center"
      bg={iconBg}
      borderColor="appBorder"
      borderWidth={1}
      boxSize={8}
      color="dimmed"
      justify="center"
      rounded="md"
      shrink={0}
    >
      <RiFlagLine size={16} />
    </Flex>
  );
}
